import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const getKey = (item) => item.cartKey || item.id;

const MAX_ONE_PER_ORDER = ['bac-water'];

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('cwf-cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('cwf-cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, variant = null) => {
    const cartKey = variant ? `${product.id}__${variant.label}` : product.id;
    setCart(prev => {
      const existing = prev.find(item => getKey(item) === cartKey);
      if (existing) {
        if (MAX_ONE_PER_ORDER.includes(product.id)) return prev;
        return prev.map(item =>
          getKey(item) === cartKey ? { ...item, qty: item.qty + 1 } : item
        );
      }
      return [...prev, {
        ...product,
        cartKey,
        variantLabel: variant?.label || null,
        variantImage: variant?.image || null,
        qty: 1
      }];
    });
  };

  const removeFromCart = (key) => {
    setCart(prev => prev.filter(item => getKey(item) !== key));
  };

  const updateQty = (key, qty, newPrice) => {
    if (qty < 1) {
      removeFromCart(key);
      return;
    }
    setCart(prev =>
      prev.map(item => {
        if (getKey(item) !== key) return item;
        const capped = MAX_ONE_PER_ORDER.includes(item.id) ? 1 : qty;
        return { ...item, qty: capped, ...(newPrice ? { price: newPrice } : {}) };
      })
    );
  };

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const cartTotal = cart.reduce((sum, item) => {
    const price = parseFloat(item.price.replace('$', '')) || 0;
    return sum + price * item.qty;
  }, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQty, clearCart, cartCount, cartTotal }}>
      {children}
    </CartContext.Provider>
  );
};
